import { useState } from "react";
import {
  linkCoApplicant,
  removeCoApplicant,
  separateCoApplicant,
  getApplicationDetail,
  makePrimaryApplicant,
} from "../../services/applicationService";
import { showSuccess, showError } from "@/lib/utils/notifications";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { OnScreenModal } from "@/components/ui/OnScreenModal";
import {
  formatApplicantId,
  formatApplicationId,
} from "../../utils/applicationFormatters";
import { formatPhoneNumber } from "../../utils/applicationFormatters";
import { useAppSelector } from "@/app/hooks";
import { selectUser } from "@/features/auth/authSlice";
import { hasPolicy } from "../../utils/applicationPolicies";
import styles from "../../styles/modals.module.css";
import type { LinkApplicantModalProps, LinkedApplicantData } from '../../types/modals'



export function LinkApplicantModal({
  applicantId,
  applicationAllData,
  applicationData,
  isCoresident,
  onClose,
  onSuccess,
}: LinkApplicantModalProps) {
  const tid = 'application-modal-link-applicant';
  const user = useAppSelector(selectUser);
  const [linkType, setLinkType] = useState<
    "Add" | "Remove" | "Separate" | null
  >(null);
  const [selectedApplicantId, setSelectedApplicantId] = useState<string>("");
  const [applicantData, setApplicantData] = useState<LinkedApplicantData | null>(null);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  /** Site-standard ConfirmDialog replaces window.confirm. */
  const [makePrimaryConfirmOpen, setMakePrimaryConfirmOpen] = useState(false);
  const [submitConfirm, setSubmitConfirm] = useState<{
    open: boolean;
    message: string;
  }>({ open: false, message: "" });

  // Check if can make primary
  const canMakePrimary =
    hasPolicy(user, ["application_detail_link_applicant"]) &&
    !applicationData?.is_primary;

  // Handle button click - set link type and show form
  const handleActionClick = (type: "Add" | "Remove" | "Separate") => {
    setLinkType(type);
    setApplicantData(null);
    setSelectedApplicantId("");
  };

  // Handle Make Primary — opens ConfirmDialog; actual work runs on confirm.
  const handleMakePrimary = () => {
    setMakePrimaryConfirmOpen(true);
  };

  const performMakePrimary = async () => {
    setMakePrimaryConfirmOpen(false);
    setLoading(true);
    try {
      await makePrimaryApplicant(applicantId);
      showSuccess("Added Successfully");
      onSuccess();
      onClose();
    } catch (error: unknown) {
      const msg = (error as { message?: unknown })?.message;
      showError(typeof msg === 'string' && msg ? msg : "Failed to make applicant primary");
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async () => {
    if (!selectedApplicantId) {
      showError("Please enter an applicant ID");
      return;
    }

    setValidating(true);
    try {
      const data = await getApplicationDetail(selectedApplicantId);
      
      let matchedApplicant = null;
      if (data?.co_resident_application) {
        const coresidents = Object.values(data.co_resident_application) as LinkedApplicantData[];
        // Compare without leading zeros by converting selectedApplicantId to Number
        const numericSearchId = String(Number(selectedApplicantId));
        matchedApplicant = coresidents.find(
          (app) => String(app.applicant_id) === numericSearchId
        ) ?? null;

        if (!matchedApplicant) {
          // Fallback to primary applicant if no exact match is found
          matchedApplicant = coresidents.find((app) => app.is_primary) ?? null;
        }
      }

      setApplicantData((matchedApplicant || data) as LinkedApplicantData);
    } catch (error: unknown) {
      const msg = (error as { message?: unknown })?.message;
      showError(typeof msg === 'string' && msg ? msg : "Failed to validate applicant");
      setApplicantData(null);
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = () => {
    if (!applicantData) {
      showError("Please validate applicant first");
      return;
    }

    let popupMessage = "Do you want to link them?";
    if (
      linkType === "Add" &&
      applicantData.rent_added &&
      applicationAllData?.rent_added
    ) {
      popupMessage = `Both parties have already paid for the contract.
      A Refund will be needed. Do you still want to link them?`;
    }
    if (linkType === "Remove") {
      let underwriterMessage = "";
      if (user?.current_role?.role_name === "ROLE_UNDERWRITER") {
        underwriterMessage = `Please complete the remove before making a decision on this application.\n`;
      }
      popupMessage = `${underwriterMessage}Are you sure want to remove Applicant?`;
    }
    if (linkType === "Separate") {
      let underwriterMessage = "";
      if (user?.current_role?.role_name === "ROLE_UNDERWRITER") {
        underwriterMessage = `Please complete the separation before making a decision on this application.\n`;
      }
      popupMessage = `${underwriterMessage}Are you sure want to Separate Applicant to New Application?`;
    }

    setSubmitConfirm({ open: true, message: popupMessage });
  };

  const performSubmit = async () => {
    setSubmitConfirm({ open: false, message: "" });
    setLoading(true);
    try {
      if (linkType === "Add") {
        const applicationId = applicationAllData.application_id;
        if (applicationId == null) {
          showError("Application id is missing");
          return;
        }
        await linkCoApplicant(applicationId, Number(selectedApplicantId));
        showSuccess("Applicant Linked Successfully!");
      } else if (linkType === "Remove") {
        await removeCoApplicant(Number(selectedApplicantId));
        showSuccess("Applicant Removed");
      } else if (linkType === "Separate") {
        const applicationId = applicationAllData.application_id;
        if (applicationId == null) {
          showError("Application id is missing");
          return;
        }
        await separateCoApplicant(applicationId, Number(selectedApplicantId));
        showSuccess("Applicant Separate Successfully!");
      }
      onSuccess();
      onClose();
    } catch (error: unknown) {
      const msg = (error as { message?: unknown })?.message;
      showError(typeof msg === 'string' && msg ? msg : "Failed to process request");
    } finally {
      setLoading(false);
    }
  };

  // Make Primary confirmation — when this is open, the action-menu popup is
  // CLOSED. Yes or No both dismiss the whole "Applicant Links" flow so the
  // menu does not reappear behind the confirm.
  if (makePrimaryConfirmOpen) {
    return (
      <ConfirmDialog
        isOpen={true}
        title="Make Primary Applicant"
        message="Do you want to make this applicant to Primary Applicant?"
        primaryLabel="Yes"
        secondaryLabel="No"
        onPrimary={() => void performMakePrimary()}
        onSecondary={onClose}
        onDismiss={onClose}
        overlayDataTestId={`${tid}-make-primary-confirm`}
        primaryButtonDataTestId={`${tid}-make-primary-confirm-btn`}
        secondaryButtonDataTestId={`${tid}-make-primary-cancel-btn`}
        closeButtonDataTestId={`${tid}-make-primary-confirm-close-button`}
      />
    );
  }

  // Show action selection screen (initial state)
  if (!linkType) {
    return (
      <OnScreenModal
        isOpen={true}
      overlayDataTestId={tid}
      panelDataTestId={`${tid}-panel`}
      titleDataTestId={`${tid}-title`}
      closeButtonDataTestId={`${tid}-close-btn`}
        title="Applicant Links"
        onClose={onClose}
        maxWidth="max-w-2xl"
      >
        <div className={styles.applicantLinksContent} data-testid={`${tid}-content`}>
            <div
              className={`${styles.modalFooterActionGroup} ${styles.modalFooterPrimaryFirst} ${styles.applicantLinksActions}`}
              data-testid={`${tid}-actions`}
            >
              <button
                className={styles.modalActionPrimary}
                onClick={() => handleActionClick("Add")}
                type="button"
                data-testid={`${tid}-link-existing-btn`}
              >
                Link Existing Applicant
              </button>
              <button
                className={styles.modalActionPrimary}
                onClick={() => handleActionClick("Remove")}
                type="button"
                data-testid={`${tid}-remove-btn`}
              >
                Remove Applicant
              </button>
              {isCoresident && (
                <button
                  className={styles.modalActionPrimary}
                  onClick={() => handleActionClick("Separate")}
                  type="button"
                  data-testid={`${tid}-separate-btn`}
                >
                  Separate Applicant
                </button>
              )}
              {canMakePrimary && (
                <button
                  className={styles.modalActionPrimary}
                  onClick={handleMakePrimary}
                  type="button"
                  disabled={loading}
                  data-testid={`${tid}-make-primary-btn`}
                >
                  Make Primary
                </button>
              )}
            </div>

            {/* Descriptions - Always show all descriptions like Angular */}
            <div className={styles.descriptionContent} data-testid={`${tid}-descriptions`}>
              <ul className={styles.descriptionList}>
                <li>
                  <strong>Link Existing Applicant</strong>: Use this when the
                  applicants exist separately but need to be linked together.
                </li>
                <li>
                  <strong>Remove Applicant</strong>: This is to delete an extra
                  version of the application. Typically occurred when a Primary
                  identified a Co-applicant, but the co-applicant started a new
                  application, which would need the Link Existing to join them
                  together.
                </li>
                <li>
                  <strong>Separate Applicant</strong>: This is to split off an
                  applicant, who may have been Denied, but the other applicants
                  were good to move forward as Approved. This keeps the denied
                  applicant in the system for a historical reference.
                </li>
                {canMakePrimary && (
                  <li>
                    <strong>Make Primary</strong>: This is to swap co-applicant
                    to primary and primary applicant to co-applicant.
                  </li>
                )}
              </ul>
            </div>
        </div>
      </OnScreenModal>
    );
  }

  const formTitle =
    linkType === "Add"
      ? "Link Existing Applicant"
      : linkType === "Remove"
        ? "Remove Applicant"
        : "Separate Applicant";

  // Show form for selected action — closing here (X or Cancel) dismisses the
  // whole flow; we do NOT go back to the "Applicant Links" action menu.
  return (
    <>
    <OnScreenModal
      isOpen={true}
      overlayDataTestId={`${tid}-form`}
      panelDataTestId={`${tid}-form-panel`}
      titleDataTestId={`${tid}-form-title`}
      closeButtonDataTestId={`${tid}-form-close-btn`}
      title={formTitle}
      onClose={onClose}
      maxWidth="max-w-2xl"
      bodyScrollOnly
      footer={
        <div
          className={`${styles.modalFooterActionGroup}${
            linkType === "Remove" ? ` ${styles.modalFooterPrimaryFirst}` : ""
          }`}
          data-testid={`${tid}-footer`}
        >
          <button
            className={`${styles.modalActionPrimary}${
              linkType === "Remove" ? ` ${styles.modalActionNoWrap}` : ""
            }`}
            onClick={handleSubmit}
            disabled={loading || !applicantData}
            type="button"
            data-testid={`${tid}-submit-btn`}
          >
            {loading ? (
              "Processing..."
            ) : (
              <>
                {linkType === "Separate"
                  ? "Separate"
                  : linkType === "Add"
                    ? "Link"
                    : "Remove"}{" "}
                Applicant
              </>
            )}
          </button>
          <button
            className={styles.modalActionSecondary}
            onClick={onClose}
            type="button"
            data-testid={`${tid}-cancel-btn`}
          >
            Cancel
          </button>
        </div>
      }
    >
      <div data-testid={`${tid}-form-body`}>
          {/* Selected Application ID */}
          <div className={styles.formGroup} data-testid={`${tid}-selected-application-id`}>
            <h6 className={styles.formLabel}>Selected Application ID</h6>
            <p className={styles.formValue}>
              {applicationAllData?.application_id != null
                ? formatApplicationId(Number(applicationAllData.application_id))
                : "N/A"}
            </p>
          </div>

          {/* Applicant Input + Validate Button — input grows, button sits flush right */}
          <div className={styles.formGroup}>
            <label htmlFor="applicantId" className={styles.formLabel} data-testid={`${tid}-applicant-id-label`}>
              Applicant<span className={styles.required}>*</span>
            </label>
            <div className="flex items-stretch gap-3">
              <input
                id="applicantId"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={selectedApplicantId}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  setSelectedApplicantId(val);
                  setApplicantData(null);
                }}
                placeholder="Applicant"
                className={`${styles.input} flex-1`}
                required
                data-testid={`${tid}-applicant-id-input`}
              />
              <button
                type="button"
                onClick={handleValidate}
                disabled={!selectedApplicantId || validating || loading}
                className={`${styles.btnPrimary} shrink-0`}
                data-testid={`${tid}-validate-btn`}
              >
                {validating ? "Validating..." : "Validate"}
              </button>
            </div>
          </div>

          {/* Applicant Information Display */}
          {applicantData && (
            <div className={styles.applicantInfo} data-testid={`${tid}-applicant-info`}>
              <div className={styles.infoRow}>
                <div className={styles.infoColumn}>
                  <h6 className={styles.infoLabel}>Name</h6>
                  <p className={styles.infoValue}>
                    {applicantData?.user_data?.first_name}{" "}
                    {applicantData?.user_data?.last_name}
                  </p>
                </div>
                <div className={styles.infoColumn}>
                  <h6 className={styles.infoLabel}>Phone Number</h6>
                  <p className={styles.infoValue}>
                    {formatPhoneNumber(applicantData?.user_data?.mobile_no ?? '')}
                  </p>
                </div>
              </div>
              <div className={styles.infoRow}>
                <div className={styles.infoColumn}>
                  <h6 className={styles.infoLabel}>Applicant ID</h6>
                  <p className={styles.infoValue}>
                    {applicantData?.applicant_id
                      ? formatApplicantId(Number(applicantData.applicant_id))
                      : "N/A"}
                  </p>
                </div>
                <div className={styles.infoColumn}>
                  <h6 className={styles.infoLabel}>Application ID</h6>
                  <p className={styles.infoValue}>
                    {applicantData?.fk_application_id
                      ? formatApplicationId(Number(applicantData.fk_application_id))
                      : "N/A"}
                  </p>
                </div>
              </div>
              <div className={styles.infoRow}>
                <div className={styles.infoColumn}>
                  <h6 className={styles.infoLabel}>Email Address</h6>
                  <p className={styles.infoValue}>
                    {applicantData?.user_data?.email || "N/A"}
                  </p>
                </div>
              </div>
              <div className={styles.infoRow}>
                <div className={styles.infoColumn}>
                  <h6 className={styles.infoLabel}>Application Status</h6>
                  <p className={styles.infoValue}>
                    {applicantData?.application_status || "N/A"}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
    </OnScreenModal>
    <ConfirmDialog
      isOpen={submitConfirm.open}
      overlayDataTestId={`${tid}-submit-confirm`}
      primaryButtonDataTestId={`${tid}-submit-confirm-btn`}
      secondaryButtonDataTestId={`${tid}-submit-cancel-btn`}
      closeButtonDataTestId={`${tid}-submit-confirm-close-button`}
      title={
        linkType === "Add"
          ? "Link Applicant"
          : linkType === "Remove"
            ? "Remove Applicant"
            : "Separate Applicant"
      }
      message={submitConfirm.message}
      primaryLabel="Yes"
      secondaryLabel="No"
      onPrimary={() => void performSubmit()}
      onSecondary={() => setSubmitConfirm({ open: false, message: "" })}
    />
    </>
  );
}
